// File: app/src/components/workout/BlockPreviewSheet.js
//
// Centered modal overlay that previews the structure of a single workout block
// (top-level phase). Triggered by tapping a MacroProgress pill. Read-only —
// does not mutate coach state.

import React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X, Repeat } from "lucide-react";
import { cn } from "lib/utils";
import { toSeconds, formatDuration } from "../../utils/time";
import useTotalDuration from "../../hooks/useTotalDuration";
import SessionPlanOutline from "./SessionPlanOutline";

const buildMacroLine = (node, totalSec) => {
  const parts = [];
  if (node?.type === "Group" && (node.value ?? 1) > 1) {
    const iterLabel = node.label ? node.label : "Rounds";
    parts.push(`${node.value}× ${iterLabel}`);
  }
  const duration = formatDuration(totalSec);
  if (duration) parts.push(`${duration} Total`);
  return parts.join(" · ");
};

const BlockPreviewSheet = ({ block, open, onOpenChange }) => {
  const node = block?.node;
  const title = block?.label || node?.label || node?.type || "Block";
  // The shared compiler, not a local sum (issue #73). The hand-rolled version
  // scored every rep-based node as zero and multiplied a time-capped Group's
  // children by its `value` — which is a seconds CAP, not an iteration count,
  // so a 60s "Row Sprint Interval" holding a 60s row read as 3600s.
  const compiledSec = useTotalDuration(node);
  // The compiler only walks containers, so a block that is a single timed leaf
  // (a lone cool-down, say) still reads its own value.
  const totalSec =
    compiledSec || (node?.children?.length ? 0 : toSeconds(node?.value, node?.unit));
  const macroLine = node ? buildMacroLine(node, totalSec) : "";

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          className={cn(
            "fixed inset-0 z-50 bg-background/80 backdrop-blur-sm", // Switched from black/80 to theme background base
            "data-[state=open]:animate-in data-[state=closed]:animate-out",
            "data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0",
          )}
        />
        <DialogPrimitive.Content
          className={cn(
            "fixed left-[50%] top-[50%] z-50 w-full max-w-sm translate-x-[-50%] translate-y-[-50%]",
            "bg-card p-6 rounded-2xl duration-200 border border-border",
            "data-[state=open]:animate-in data-[state=closed]:animate-out",
            "data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0",
            "data-[state=open]:zoom-in-95 data-[state=closed]:zoom-out-95",
            "focus:outline-none",
          )}
        >
          <div className="flex items-start justify-between gap-2 mb-1">
            <DialogPrimitive.Title className="text-2xl font-bold leading-tight text-foreground truncate drop-shadow-md">
              {title}
            </DialogPrimitive.Title>
            <DialogPrimitive.Close
              className="p-1.5 rounded-md text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              aria-label="Close block preview"
            >
              <X className="w-4 h-4" />
            </DialogPrimitive.Close>
          </div>

          {macroLine && (
            <DialogPrimitive.Description className="text-sm text-muted-foreground mb-4 flex items-center gap-1.5 font-medium tracking-wide">
              {node?.type === "Group" && (node.value ?? 1) > 1 && (
                <Repeat className="w-3.5 h-3.5 shrink-0 text-primary/70" />
              )}
              <span>{macroLine}</span>
            </DialogPrimitive.Description>
          )}

          {/* Render the tree outline scoped to just this node */}
          {node ? (
            // UPGRADE 3: Resolving the grey wash with a high-contrast recessed container and thematic borders.
            <div className="mt-4 bg-background border border-border/50 rounded-xl p-3 shadow-inner">
              <SessionPlanOutline 
                plan={{ children: [node] }} 
                maxHeight="max-h-[50vh]" 
              />
            </div>
          ) : (
            <p className="text-sm text-muted-foreground italic mt-4">
              No movements to preview.
            </p>
          )}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
};

export default BlockPreviewSheet;